// src/components/Warden.jsx
import {
  SignedIn,
  SignedOut,
  RedirectToSignIn,
  useClerk,
  useUser,
} from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import axios from "axios";

const API_URL = "https://bcm-backend-9ca9.onrender.com";

export default function Warden() {
  const { signOut } = useClerk();
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState("messages");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <>
      <SignedIn>
        <div className="flex min-h-screen">
          {/* Mobile Menu */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden fixed top-4 left-4 z-50 bg-maroon text-white p-2 rounded-lg shadow-lg"
          >
            {sidebarOpen ? "✕" : "☰"}
          </button>

          {/* Sidebar */}
          <aside
            className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-maroon text-white p-4 sm:p-6 space-y-4 transform transition-transform duration-300 ease-in-out ${
              sidebarOpen ? "translate-x-0" : "-translate-x-full"
            } lg:translate-x-0`}
          >
            <div className="mt-12 lg:mt-0">
              <h1 className="text-xl sm:text-2xl font-bold">Warden Panel</h1>
              <p className="text-xs sm:text-sm mt-2">
                Welcome, {user?.fullName || "Warden"}
              </p>
            </div>

            <nav className="flex flex-col gap-2 sm:gap-3 mt-6">
              {["messages", "events", "updates"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    setSidebarOpen(false);
                  }}
                  className={`text-left px-3 py-2 rounded-lg text-sm sm:text-base ${
                    activeTab === tab
                      ? "bg-white text-maroon font-bold"
                      : "hover:bg-maroonDark"
                  }`}
                >
                  {tab === "messages" && "📧 Contact Messages"}
                  {tab === "events" && "📅 Manage Events"}
                  {tab === "updates" && "🔔 Important Updates"}
                </button>
              ))}

              <button
                onClick={() => signOut(() => (window.location.href = "/"))}
                className="mt-4 sm:mt-6 bg-red-500 hover:bg-red-600 px-4 py-2 rounded-lg text-sm sm:text-base"
              >
                Logout
              </button>
            </nav>
          </aside>

          {/* Overlay for mobile */}
          {sidebarOpen && (
            <div
              className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
              onClick={() => setSidebarOpen(false)}
            ></div>
          )}

          {/* Main Content */}
          <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-gray-50 w-full lg:ml-0">
            {activeTab === "messages" && <MessagesManager />}
            {activeTab === "events" && <EventsManager />}
            {activeTab === "updates" && <UpdatesManager />}
          </main>
        </div>
      </SignedIn>

      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

// ✅ Keep your MessagesManager, EventsManager, and UpdatesManager exactly as before (your code was correct)

/* ---------------- Contact Messages Manager ---------------- */
function MessagesManager() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchMessages = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_URL}/messages`);
      setMessages(res.data || []);
    } catch (err) {
      console.error("Failed to fetch messages:", err);
      setError("Failed to load messages. Check your backend server.");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    try {
      await axios.delete(`${API_URL}/messages/${id}`);
      fetchMessages();
    } catch (err) {
      console.error("Failed to delete message:", err);
      alert("Failed to delete message.");
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-maroon mb-6">
        📥 User Contact Messages
      </h2>

      {error && <p className="text-red-600 mb-4 font-medium">{error}</p>}
      
      <button 
        onClick={fetchMessages} 
        disabled={loading}
        className="bg-maroon text-white px-4 py-2 rounded-lg mb-4 hover:bg-maroonDark transition disabled:opacity-50 text-sm"
      >
        {loading ? "Refreshing..." : "Refresh Messages"}
      </button>

      {loading && messages.length === 0 ? (
        <p className="text-gray-600">Loading messages...</p>
      ) : messages.length === 0 ? (
        <p className="text-gray-500 text-center py-8 text-sm sm:text-base bg-white rounded-lg shadow">
          No contact messages have been submitted yet.
        </p>
      ) : (
        <div className="space-y-4">
          {messages.map((msg) => (
            <div
              key={msg._id}
              className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5 shadow-sm hover:shadow-md transition"
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold text-lg text-maroon break-words">
                  {msg.name}
                </h3>
                <button
                  onClick={() => handleDelete(msg._id)}
                  className="text-red-500 hover:text-red-700 font-bold text-xl ml-4"
                  title="Delete Message"
                >
                  ✕
                </button>
              </div>
              <p className="text-sm text-gray-600 italic mb-3 break-words">
                Email: <a href={`mailto:${msg.email}`} className="text-blue-500 hover:underline">{msg.email}</a>
              </p>
              <div className="bg-gray-50 p-3 rounded-md border text-gray-800 break-words">
                <p className="font-semibold text-sm mb-1">Message:</p>
                <p className="text-base">{msg.message}</p>
              </div>
              <p className="text-xs text-right text-gray-400 mt-2">
                Received: {msg.createdAt ? formatTimestamp(msg.createdAt) : 'N/A'}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Events Manager ---------------- */
function EventsManager() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({
    title: "",
    date: "",
    description: "",
    image: null,
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/events`);
      setEvents(res.data || []);
    } catch (err) {
      console.error("Failed to fetch events:", err);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

 const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image" && files && files[0]) {
      setForm((prev) => ({ ...prev, image: files[0] }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("title", form.title);
      formData.append("date", form.date);
      formData.append("description", form.description);
      if (form.image) formData.append("image", form.image);

      if (editingId) {
        await axios.put(`${API_URL}/events/${editingId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessage({ text: "Event updated successfully!", type: "success" });
        setEditingId(null);
      } else {
        await axios.post(`${API_URL}/events`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessage({ text: "Event added successfully!", type: "success" });
      }

      setForm({ title: "", date: "", description: "", image: null });
      fetchEvents();
    } catch (err) {
      console.error(err);
      setMessage({ text: "Something went wrong!", type: "error" });
    }

    setLoading(false);
    setTimeout(() => setMessage({ text: "", type: "" }), 3000);
  };

  const handleEdit = (ev) => {
    setEditingId(ev._id);
    setForm({
      title: ev.title,
      date: ev.date,
      description: ev.description,
      image: null,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    try {
      await axios.delete(`${API_URL}/events/${id}`);
      setMessage({ text: "Event deleted successfully!", type: "warning" });
      fetchEvents();
    } catch (err) {
      console.error(err);
      setMessage({ text: "Failed to delete event!", type: "error" });
    }
    setTimeout(() => setMessage({ text: "", type: "" }), 3000);
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      {message.text && (
        <div
          className={`mb-4 p-3 rounded-lg text-center font-medium text-sm sm:text-base ${
            message.type === "success"
              ? "bg-green-100 text-green-800"
              : message.type === "error"
              ? "bg-red-100 text-red-800"
              : "bg-yellow-100 text-yellow-800"
          }`}
        >
          {message.text}
        </div>
      )}

      <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-maroon mb-4">
        Manage Events
      </h2>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white shadow rounded-lg p-4 sm:p-5 lg:p-6 mb-6 border border-gray-300 space-y-4"
        encType="multipart/form-data"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <input
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="Event Title"
            required
            className="w-full border px-3 py-2 rounded-lg focus:ring-2 focus:ring-maroon text-sm sm:text-base"
          />
          <input
  type="date"
  name="date"
  value={form.date}
  onChange={handleChange}
  required
  className="w-full border px-3 py-2 rounded-lg focus:ring-2 focus:ring-maroon text-sm sm:text-base"
/>

          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="Description (max 250 chars)"
            rows="3"
            maxLength={250}
            required
            className="sm:col-span-2 w-full border px-3 py-2 rounded-lg focus:ring-2 focus:ring-maroon text-sm sm:text-base"
          />
          <input
            type="file"
            name="image"
            accept="image/*"
            onChange={handleChange}
            className="sm:col-span-2 w-full border px-3 py-2 rounded-lg focus:ring-2 focus:ring-maroon text-sm sm:text-base"
          />
          {form.image && (
            <img
              src={URL.createObjectURL(form.image)}
              alt="Preview"
              className="sm:col-span-2 w-full sm:w-60 h-40 object-contain rounded-lg border mt-2"
            />
          )}
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => {
              setForm({ title: "", date: "", description: "", image: null });
              setEditingId(null);
            }}
            className="w-full sm:w-auto border border-maroon text-maroon px-4 sm:px-6 py-2 rounded-lg hover:bg-maroon hover:text-white text-sm sm:text-base"
          >
            Clear
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`w-full sm:w-auto px-4 sm:px-6 py-2 rounded-lg text-white text-sm sm:text-base ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-maroon hover:bg-maroonDark"
            }`}
          >
            {loading
              ? "Saving..."
              : editingId
              ? "Update Event"
              : "Add Event"}
          </button>
        </div>
      </form>

      {/* Existing Events */}
      <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-4">
        Existing Events
      </h2>
      <div className="space-y-4">
        {events.length === 0 ? (
          <p className="text-gray-500 text-center py-8 text-sm sm:text-base">
            No events yet. Add your first event above!
          </p>
        ) : (
          events.map((ev) => (
            <div
              key={ev._id}
              className="bg-gray-100 rounded-lg p-3 sm:p-4 flex flex-col md:flex-row items-start gap-3 sm:gap-4"
            >
              {ev.imageUrl && (
                <img
                  src={ev.imageUrl}
                  alt={ev.title}
                  className="w-full md:w-32 lg:w-40 h-32 sm:h-40 object-cover rounded-lg"
                />
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-maroon text-base sm:text-lg break-words">
                  {ev.title}
                </h3>
                <p className="text-xs sm:text-sm italic text-gray-600 mt-1">
                  {ev.date}
                </p>
                <p className="mt-1 sm:mt-2 text-gray-800 text-sm sm:text-base break-words">
                  {ev.description}
                </p>
              </div>
              <div className="flex flex-row md:flex-col gap-2 w-full md:w-auto mt-2 md:mt-0">
                <button
                  onClick={() => handleEdit(ev)}
                  className="flex-1 md:flex-none bg-gray-300 hover:bg-gray-400 text-gray-800 px-3 py-1.5 rounded-md text-sm sm:text-base"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(ev._id)}
                  className="flex-1 md:flex-none bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-md text-sm sm:text-base"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ---------------- Updates Manager ---------------- */
function UpdatesManager() {
  const [updates, setUpdates] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchUpdates = async () => {
    try {
      const res = await axios.get(`${API_URL}/updates`);
      setUpdates(res.data || []);
    } catch (err) {
      console.error("Failed to fetch updates:", err);
    }
  };

  useEffect(() => {
    fetchUpdates();
  }, []);

  const addUpdate = async (e) => {
    e.preventDefault();
    if (!newMsg.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/updates`, { message: newMsg });
      setNewMsg("");
      fetchUpdates();
    } catch (err) {
      console.error("Failed to add update:", err);
    }
    setLoading(false);
  };

  const deleteUpdate = async (id) => {
    if (!window.confirm("Delete this update?")) return;
    try {
      await axios.delete(`${API_URL}/updates/${id}`);
      fetchUpdates();
    } catch (err) {
      console.error("Failed to delete update:", err);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 text-maroon">
        Manage Important Updates
      </h2>
      <form onSubmit={addUpdate} className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-6">
        <input
          type="text"
          value={newMsg}
          onChange={(e) => setNewMsg(e.target.value)}
          placeholder="Write update message..."
          className="flex-1 border px-3 py-2 rounded-lg text-sm sm:text-base"
          maxLength={200}
        />
        <button
          disabled={loading}
          className={`bg-maroon text-white px-4 sm:px-6 py-2 rounded-lg text-sm sm:text-base ${
            loading ? "opacity-50 cursor-not-allowed" : "hover:bg-maroonDark"
          }`}
        >
          {loading ? "Adding..." : "Add"}
        </button>
      </form>
      <ul className="space-y-3">
        {updates.length === 0 ? (
          <p className="text-gray-500 text-center py-8 text-sm sm:text-base">
            No updates yet. Add your first update above!
          </p>
        ) : (
          updates.map((u) => (
            <li
              key={u._id}
              className="bg-white border rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3"
            >
              <span className="flex-1 break-words text-sm sm:text-base pr-2">
                {u.message}
              </span>
              <button
                onClick={() => deleteUpdate(u._id)}
                className="text-red-500 hover:text-red-700 font-bold text-lg self-end sm:self-center"
              >
                ✕
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
